import React, { useCallback } from 'react';
import {
  PropertyConfigurationForms,
  propertyConfigurationService,
  PropertyConfiguration,
  Employee,
} from 'app/state';
import {
  PropertyConfigurationForm,
  PropertyConfigurationFormFields,
} from './property-configuration.form';
import { FormHub, useFormEvents } from '@lib/common';

interface Props {
  property: PropertyConfiguration;
  managers: Array<Employee>;
  managersLoading?: boolean;
}

export const ManagePropertyConfigurationComponent: React.FC<Props> = ({
  property,
  managers,
  managersLoading,
}) => {
  const [{ status, error }, reset] = useFormEvents(PropertyConfigurationForms.Update);

  const submit = useCallback(
    ({
      name,
      propertyCode,
      location,
      contact,
      groupBooking,
      taxRates,
      salesTaxRates,
      checkIn,
      checkOut,
      earlyCheckIn,
      lateCheckOut,
      changeableThreshold,
      cancelableThreshold,
      taxCreditPeriod,
      features,
      useSalesTaxForMealCosts,
      guestSurveyManagerIds,
      rateThresholdPercentage,
    }: PropertyConfigurationFormFields) => {
      propertyConfigurationService.updateProperty(property.id, {
        name,
        propertyCode,
        location: location,
        contact,
        groupBooking,
        taxRates,
        salesTaxRates,
        checkIn,
        checkOut,
        earlyCheckIn,
        lateCheckOut,
        changeableThreshold,
        cancelableThreshold,
        taxCreditPeriod,
        features: Object.entries(features)
          .filter(([_, value]) => value)
          .map(([key]) => key),
        useSalesTaxForMealCosts,
        guestSurveyManagerIds,
        rateThresholdPercentage,
      });
    },
    [property]
  );

  return (
    <FormHub<PropertyConfigurationFormFields>
      formOptions={{
        defaultValues: {
          name: property.name,
          propertyCode: property.propertyCode,
          contact: property.contact,
          location: property.location,
          groupBooking: property.groupBooking,
          backOfficeEmail: property.backOfficeEmail ?? '',
          reportEmail: property.reportEmail ?? '',
          maintenanceEmail: property.maintenanceEmail ?? '',
          maintenancePhone: property.maintenancePhone ?? '',
          guestSurveyManagerIds: property.guestSurveyManagerIds ?? [],
          checkIn: property.checkIn,
          checkOut: property.checkOut,
          earlyCheckIn: property.earlyCheckIn,
          lateCheckOut: property.lateCheckOut,
          changeableThreshold: property.changeableThreshold,
          cancelableThreshold: property.cancelableThreshold,
          taxRates: property.taxRates,
          taxCreditPeriod: property.taxCreditPeriod ?? 0,
          features:
            property.features?.reduce((r, flag) => {
              r[flag] = true;
              return r;
            }, {} as PropertyConfigurationFormFields['features']) ?? {},
          useSalesTaxForMealCosts: property.useSalesTaxForMealCosts,
          rateThresholdPercentage: property.rateThresholdPercentage ?? 0,
        },
      }}
      onSubmit={submit}
    >
      <PropertyConfigurationForm
        property={property}
        managers={managers}
        managersLoading={managersLoading}
        status={status}
        error={error}
        resetFormUI={reset}
      />
    </FormHub>
  );
};
